import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTable, usePagination, useBlockLayout } from 'react-table'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'

import guidesLogo from 'Assets/images/empty-guides.svg?url'

import useOutsideClickListener from 'LegacyUtils/hooks/useOutsideClickListener'

import { fetchGuides, deleteGuide } from 'Actions/guides'
import { getAllGuides, getGuidesTotalCount } from 'Selectors/guides'
import searchIcon from 'Assets/images/search.svg?url'

const Options = ({ id }) => {
  const [show, setShow] = useState(false)
  const ref = useRef()

  const dispatch = useDispatch()
  const closeHandler = useCallback(() => setShow(false), [setShow])

  useOutsideClickListener(ref, closeHandler, show)

  const onDeleteClick = useCallback(
    (e) => {
      e.preventDefault()
      const canDelete = confirm('Are you sure you want to delete this guide? This cannot be undone.')
      if (!canDelete) return
      dispatch(deleteGuide({ guideId: id }))
    },
    [dispatch, id]
  )

  const defaultClick = useCallback(
    (e) => {
      e.preventDefault()
      setShow(!show)
    },
    [show]
  )

  return (
    <div className='options-dots' onClick={defaultClick}>
      {show && (
        <div className='dropdown-menu' ref={ref}>
          <span className='dropdown-menu-arrow' />
          <a className='dropdown-item' onClick={onDeleteClick}>
            Delete
          </a>
        </div>
      )}
    </div>
  )
}

const GuidesList = () => {
  const guides = useSelector(getAllGuides)
  const totalGuidesCount = useSelector(getGuidesTotalCount)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [pageCount, setPageCount] = useState(0)
  const [searchText, setSearchText] = useState('')

  const columns = useMemo(() => {
    return [
      {
        Header: 'NAME',
        accessor: 'name'
      },
      {
        Header: 'CREATED BY',
        accessor: 'user.name'
      },
      {
        Header: 'CREATED AT',
        accessor: (row) => row,
        Cell: ({ cell: { value } }) => {
          const lastCreated = value.created_at
          return moment(lastCreated).fromNow()
        }
      },
      {
        Header: 'LAST UPDATED',
        accessor: (row) => row,
        Cell: ({ cell: { value } }) => {
          const lastUpdated = value.updated_at || value.created_at
          return moment(lastUpdated).fromNow()
        }
      }
    ]
  }, [])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    gotoPage,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data: guides,
      initialState: { pageIndex: 0, pageSize: 15 },
      manualPagination: true,
      pageCount
    },
    usePagination,
    useBlockLayout
  )

  useEffect(() => {
    const offset = pageIndex * pageSize
    const limit = pageSize

    dispatch(fetchGuides({ offset, limit, search: searchText }))
  }, [dispatch, pageIndex, pageSize, searchText])

  // Reset PageIndex to zero for search filter
  useEffect(() => {
    gotoPage(0)
  }, [searchText, gotoPage])

  useEffect(() => {
    setPageCount(Math.ceil(totalGuidesCount / pageSize))
  }, [guides, totalGuidesCount, pageSize])

  const onSearchGuides = (event) => {
    if (event.key !== 'Enter') {
      return
    }
    const { value } = event.target
    setSearchText(value)
  }

  return (
    <div className='guides-container'>
      <div className='page-top-bar'>
        <div className='title'>Guides</div>
        {rows.length > 0 && (
          <div className='right'>
            <fieldset className='search-container'>
              <img src={searchIcon} className='search-icon' alt='' />
              <input
                className='search-input'
                onKeyDown={onSearchGuides}
                type='text'
                placeholder='Type and hit enter to search'
              />
            </fieldset>
            <button className='button-primary mr-3' onClick={() => navigate('create')}>
              New Guide
            </button>
            <div className='pagination'>
              <button
                className='previous-button pagination-button mr-1'
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                ‹
              </button>{' '}
              <button className='next-button pagination-button' onClick={() => nextPage()} disabled={!canNextPage}>
                ›
              </button>{' '}
            </div>
          </div>
        )}
      </div>
      {rows.length > 0 && (
        <div>
          <div className='list-container'>
            <div {...getTableProps()} className='table'>
              <div className='thead'>
                {headerGroups.map((headerGroup) => {
                  const { key } = headerGroup.getHeaderGroupProps()
                  return (
                    <div className='tr' key={key} {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => {
                        const { key } = column.getHeaderProps()
                        return (
                          <div className='th' {...column.getHeaderProps()} key={key}>
                            {column.render('Header')}
                          </div>
                        )
                      })}
                      <div className='th options-column' />
                    </div>
                  )
                })}
              </div>
              <div className='tbody' {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row)
                  const { key, ...rowProps } = row.getRowProps()
                  const id = row.original.id

                  return (
                    <div {...rowProps} key={key} className='pointer tr'>
                      {row.cells.map((cell) => {
                        const { key, ...cellProps } = cell.getCellProps()
                        return (
                          <div className='td' {...cellProps} key={key} onClick={() => navigate(`edit/${id}`)}>
                            {cell.render('Cell')}
                          </div>
                        )
                      })}
                      <div className='options td'>
                        <Options id={id} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      {rows.length === 0 && searchText !== '' && (
        <div style={{ display: 'grid', height: '80vh' }}>
          <span style={{ margin: 'auto' }}>No guides found</span>
        </div>
      )}
      {totalGuidesCount === 0 && !searchText.length && (
        <div className='empty-state'>
          <div className='empty-state-content'>
            <img className='mb-3' src={guidesLogo} alt='' />
            <h1>Guides help you simplify your articles into steps</h1>
            <p>
              With guides, you can help your users troubleshoot their issues with bite-sized support
              <br />
              content in a step-by-step manner.
            </p>
            <button onClick={() => navigate('create')} className='button-primary mb-3'>
              Create a new Guide
            </button>
            <br />
            {/* <a href=''>Learn more about guides</a> */}
          </div>
        </div>
      )}
    </div>
  )
}

export default GuidesList
